import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { LANGS } from "@/i18n/translations";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/method", label: t.nav.method || "Method" },
    { to: "/services", label: t.nav.services },
    { to: "/hospitality", label: t.nav.hospitality || "Hospitality" },
    { to: "/spa-at-home", label: t.nav.spa },
    { to: "/institutional", label: t.nav.institutional || "Institutional" },
    { to: "/testimonials", label: t.nav.testimonials },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#070E13]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 h-20 flex items-center justify-between text-[#F4EEE8]">
        <Link to="/" data-testid="logo-link" className="flex items-center group shrink-0">
          <img
            src="/logo-white.png"
            alt="Carol Stoffella"
            className="h-4 sm:h-5 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav — only on xl+ to avoid overflow with 8 links + CTA */}
        <nav className="hidden xl:flex items-center gap-5 ms-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `overline link-underline whitespace-nowrap transition-colors ${
                  isActive ? "text-[#C7A26C]" : "text-[#F4EEE8]/80 hover:text-[#F4EEE8]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:flex items-center gap-1 text-xs">
            {LANGS.map((l, i) => (
              <button
                key={l.code}
                data-testid={`language-toggle-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2 py-1 tracking-widest transition-colors ${
                  lang === l.code ? "text-[#C7A26C]" : "text-[#F4EEE8]/60 hover:text-[#F4EEE8]"
                } ${i > 0 ? 'before:content-["/"] before:mx-1 before:text-[#F4EEE8]/30' : ""}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="cta-premium hidden md:inline-flex items-center px-5 lg:px-6 py-2.5 lg:py-3 text-[0.68rem] lg:text-xs tracking-[0.2em] uppercase font-medium bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] whitespace-nowrap"
          >
            {t.common.bookConsultation}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden text-[#F4EEE8] p-2"
            aria-label="Open menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile / Tablet sheet — shows below xl breakpoint */}
      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-[#070E13] border-t border-white/10 text-[#F4EEE8]">
          <nav className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `font-serif text-3xl ${isActive ? "text-[#C7A26C]" : "text-[#F4EEE8]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 flex items-center gap-3 text-xs tracking-widest">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-language-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 border ${
                    lang === l.code
                      ? "border-[#C7A26C] text-[#C7A26C]"
                      : "border-white/20 text-[#F4EEE8]/70"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              className="cta-premium mt-4 self-start inline-flex items-center px-7 py-3 text-xs tracking-[0.22em] uppercase font-medium bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13]"
            >
              {t.common.bookConsultation}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
